/**
 * Plate country helpers for free valuation.
 * Default market: Ireland (IE). UK and Other supported as overrides.
 */

export const PLATE_COUNTRIES = [
  { code: "IE", label: "Ireland" },
  { code: "UK", label: "UK" },
  { code: "OTHER", label: "Other" },
];

export const DEFAULT_PLATE_COUNTRY = "IE";

export const REG_MIN_LENGTH = 3;
export const REG_MAX_LENGTH = 20;
/** Display length with separators (e.g. 191-D-123456). */
export const REG_INPUT_MAX_LENGTH = 24;

/** Strip to A–Z / 0–9 only (validation / API normalize). */
export function normalizeRegNumber(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/** Keep letters, digits, and hyphens while typing. */
export function sanitizeRegInput(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .replace(/-{2,}/g, "-");
}

/**
 * Irish formats (normalized, no separators):
 * - Modern (2013+): YYH + county(1–2) + seq  e.g. 191D12345
 * - Older (1987–2012): YY + county(1–2) + seq  e.g. 12D1234
 */
const IE_MODERN =
  /^([0-9]{2}[12])([A-Z]{1,2})([0-9]{1,6})$/;
const IE_LEGACY =
  /^([0-9]{2})([A-Z]{1,2})([0-9]{1,6})$/;

/** Current GB style: AB12CDE */
const UK_CURRENT =
  /^[A-HJ-PR-Y]{2}[0-9]{2}[A-HJ-PR-Z]{3}$/;
/** Common older GB styles (prefix / suffix), simplified */
const UK_PREFIX = /^[A-HJ-PR-Y][0-9]{1,3}[A-HJ-PR-Z]{3}$/;
const UK_SUFFIX = /^[A-HJ-PR-Z]{3}[0-9]{1,3}[A-HJ-PR-Y]$/;

export function isIrishPlate(normalized) {
  const n = normalizeRegNumber(normalized);
  if (IE_MODERN.test(n)) return true;
  return IE_LEGACY.test(n) && !IE_MODERN.test(n);
}

export function isUkPlate(normalized) {
  const n = normalizeRegNumber(normalized);
  return UK_CURRENT.test(n) || UK_PREFIX.test(n) || UK_SUFFIX.test(n);
}

/** Soft guess from typed plate; null if unclear. */
export function suggestPlateCountry(value) {
  const n = normalizeRegNumber(value);
  if (!n || n.length < 5) return null;
  if (isIrishPlate(n)) return "IE";
  if (isUkPlate(n)) return "UK";
  return null;
}

/**
 * Best country match once the user leaves the field.
 * Returns IE / UK / OTHER, or null when empty / too short.
 */
export function detectPlateCountry(value) {
  const n = normalizeRegNumber(value);
  if (!n || n.length < REG_MIN_LENGTH) return null;
  if (isIrishPlate(n)) return "IE";
  if (isUkPlate(n)) return "UK";
  return "OTHER";
}

export function getPlateCountryLabel(code) {
  return PLATE_COUNTRIES.find((c) => c.code === code)?.label || code;
}

/**
 * Identify likely plate country for UI hints (including when "Other" is selected).
 * Returns { code, label } or null.
 */
export function identifyPlateCountry(value) {
  const n = normalizeRegNumber(value);
  if (!n || n.length < REG_MIN_LENGTH) return null;

  if (isIrishPlate(n)) {
    return { code: "IE", label: "Ireland" };
  }
  if (isUkPlate(n)) {
    return { code: "UK", label: "United Kingdom" };
  }

  // Extra format hints when the user picks Other (not used for hard validation)
  const extras = [
    {
      code: "NL",
      label: "Netherlands",
      test: (p) =>
        /^[A-Z]{2}[0-9]{2}[A-Z]{2}$/.test(p) ||
        /^[0-9]{2}[A-Z]{3}[0-9]$/.test(p) ||
        /^[A-Z][0-9]{3}[A-Z]{2}$/.test(p),
    },
    {
      code: "FR",
      label: "France",
      test: (p) => /^[A-Z]{2}[0-9]{3}[A-Z]{2}$/.test(p),
    },
    {
      code: "ES",
      label: "Spain",
      test: (p) => /^[0-9]{4}[A-Z]{3}$/.test(p),
    },
    {
      code: "IT",
      label: "Italy",
      test: (p) => /^[A-Z]{2}[0-9]{3}[A-Z]{2}$/.test(p),
    },
    {
      code: "DE",
      label: "Germany",
      // City letters + 1–2 letters + 1–4 digits (simplified)
      test: (p) =>
        p.length >= 4 &&
        p.length <= 8 &&
        /^[A-Z]{1,3}[A-Z]{1,2}[0-9]{1,4}$/.test(p) &&
        !isUkPlate(p),
    },
    {
      code: "PL",
      label: "Poland",
      test: (p) => /^[A-Z]{2,3}[0-9]{4,5}$/.test(p) || /^[A-Z]{2}[0-9]{5}$/.test(p),
    },
  ];

  for (const item of extras) {
    if (item.test(n)) {
      return { code: item.code, label: item.label };
    }
  }

  return null;
}

/** Human-readable blur hint for the detected plate country. */
export function getDetectedCountryHint(value, selectedCountry = DEFAULT_PLATE_COUNTRY) {
  const identified = identifyPlateCountry(value);
  if (!identified) {
    if (selectedCountry === "OTHER" && normalizeRegNumber(value)) {
      return "Could not identify which country this plate belongs to.";
    }
    return "";
  }

  if (selectedCountry === "OTHER") {
    return `This plate belongs to ${identified.label}.`;
  }

  if (selectedCountry === identified.code ||
      (selectedCountry === "UK" && identified.code === "UK") ||
      (selectedCountry === "IE" && identified.code === "IE")) {
    return `Detected country: ${identified.label}.`;
  }

  return `This plate looks like ${identified.label} (you selected ${getPlateCountryLabel(selectedCountry)}).`;
}

/** Pretty-print for display (IE with hyphens; Other keeps typed hyphens). */
export function formatRegForDisplay(value, country = DEFAULT_PLATE_COUNTRY) {
  const raw = sanitizeRegInput(value);
  const n = normalizeRegNumber(value);
  if (!n) return raw;

  if (country === "IE") {
    const modern = n.match(IE_MODERN);
    if (modern) return `${modern[1]}-${modern[2]}-${modern[3]}`;
    const legacy = n.match(IE_LEGACY);
    if (legacy && !IE_MODERN.test(n)) {
      return `${legacy[1]}-${legacy[2]}-${legacy[3]}`;
    }
    // Incomplete Irish plate — keep hyphens the user typed
    return raw;
  }

  if (country === "UK") {
    if (UK_CURRENT.test(n)) {
      return `${n.slice(0, 4)} ${n.slice(4)}`;
    }
    return raw;
  }

  // OTHER — allow hyphens as typed
  return raw;
}

/** Value sent to check_reg API. */
export function formatRegForApi(value, country = DEFAULT_PLATE_COUNTRY) {
  const n = normalizeRegNumber(value);
  if (!n) return "";
  if (country === "IE") {
    const display = formatRegForDisplay(n, "IE");
    // Prefer hyphenated Irish form when structure is clear
    if (display.includes("-")) return display;
  }
  if (country === "OTHER") {
    // Keep hyphens the user entered for other markets
    return sanitizeRegInput(value);
  }
  return n;
}

export function getPlatePlaceholder(country) {
  if (country === "UK") return "e.g. AB12 CDE";
  if (country === "OTHER") return "Enter registration";
  return "e.g. 191-D-12345";
}

/** Map API / UI aliases to IE | UK | OTHER. */
export function normalizePlateCountryCode(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";

  if (
    raw === "IE" ||
    raw === "IRL" ||
    raw === "IRELAND" ||
    raw === "IRISH" ||
    raw === "ROI" ||
    raw === "EIRE"
  ) {
    return "IE";
  }

  if (
    raw === "UK" ||
    raw === "GB" ||
    raw === "GBR" ||
    raw === "UNITED KINGDOM" ||
    raw === "GREAT BRITAIN" ||
    raw === "ENGLAND" ||
    raw === "SCOTLAND" ||
    raw === "WALES" ||
    raw === "NI" ||
    raw === "NORTHERN IRELAND"
  ) {
    return "UK";
  }

  if (raw === "OTHER" || raw === "INTL" || raw === "INTERNATIONAL") {
    return "OTHER";
  }

  return "";
}

/** Prefer saved plate country; fall back to detecting from the reg string. */
export function resolveEvaluationPlateCountry({
  plateCountry,
  evaluation,
  reg,
} = {}) {
  const candidates = [
    plateCountry,
    evaluation?.plate_country,
    evaluation?.Plate_country,
    evaluation?.country,
  ];

  for (const value of candidates) {
    const code = normalizePlateCountryCode(value);
    if (code) {
      return code;
    }
  }

  return detectPlateCountry(reg) || DEFAULT_PLATE_COUNTRY;
}

export function getRegValidationError(value, country = DEFAULT_PLATE_COUNTRY) {
  const n = normalizeRegNumber(value);
  if (!n) return "Please enter a registration number.";
  if (n.length < REG_MIN_LENGTH) {
    return `Registration must be at least ${REG_MIN_LENGTH} characters.`;
  }
  if (n.length > REG_MAX_LENGTH) {
    return `Registration must be at most ${REG_MAX_LENGTH} characters.`;
  }

  if (country === "IE") {
    if (!isIrishPlate(n)) {
      return "Enter a valid Irish plate (e.g. 191-D-12345).";
    }
    return "";
  }

  if (country === "UK") {
    if (!isUkPlate(n)) {
      return "Enter a valid UK plate (e.g. AB12 CDE).";
    }
    return "";
  }

  // OTHER — alphanumeric length only
  return "";
}
